// Shared by the app (app/src/lib/fit.ts re-exports it) and the edge functions (browse's date filter): which places and
// events fit a day, and which places only happen on set dates (monthly, first Thursday, every 2nd Saturday...). Pure, no
// imports, so the app, Deno and tests/plan_v3_unit_test.ts all load the same file. Plan v3 (R-73) + QA rounds 1-2.
// Days are compared as calendar dates in the city's own zone: an 8 PM Los Angeles event is on its LA day even though
// its UTC start is the next day (the prod r1 date-fit bug, build/qa/2026-10-08-prod-r1).
// One import: hourstext.ts (pure too), so opening hours written as text read the same here and on the server.
import { hoursFromText } from './hourstext.ts'
export type FitItem = { kind: string; start_at: string | null; end_at: string | null; hours?: unknown; title?: string; one_line?: string | null; schedule_text?: string | null; hours_text?: string | null }
export type FitEntry = { item: FitItem; gone?: string; tz: string | null }

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
const fmtCache = new Map<string, Intl.DateTimeFormat>()
/** "2026-10-10" for an instant, in a city's zone. */
export function ymdIn(iso: string | Date, tz: string | null | undefined): string {
  const zone = tz || 'America/Los_Angeles'
  let f = fmtCache.get(zone)
  if (!f) { f = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }); fmtCache.set(zone, f) }
  return f.format(typeof iso === 'string' ? new Date(iso) : iso)
}
const noon = (ymd: string) => new Date(`${ymd}T12:00:00Z`)
export const dowOfYmd = (ymd: string) => noon(ymd).getUTCDay()
export function plusDays(ymd: string, n: number): string {
  const d = noon(ymd)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
/** Today in a city's zone. */
export const todayIn = (tz: string | null | undefined) => ymdIn(new Date(), tz)

/** Opening hours the item really has (the same test the server uses for "known hours"), else null. */
function knownHours(it: FitItem): Record<string, number[][]> | null {
  const h = it.hours as (Record<string, unknown> & { unclear?: boolean }) | null | undefined
  if (!h || h.unclear) return null
  const days = Object.entries(h).filter(([k, v]) => DAY_KEYS.includes(k) && Array.isArray(v))
  return days.length ? (Object.fromEntries(days) as Record<string, number[][]>) : null
}
const RUN_MS = 12 * 3600e3
/** Opening hours the way the server reads them (times.ts usableHours): structured hours when clear, else the place's own
 *  hours text. B-089 review: lateToday / daytimeStart must not call a place with "Tue-Sat 10am-5pm" text open at 9 PM. */
function usableHours(it: FitItem): Record<string, number[][]> | null {
  const k = knownHours(it)
  if (k) return k
  const t = hoursFromText(it.hours_text)
  if (!t) return null
  const days = Object.entries(t).filter(([d, v]) => DAY_KEYS.includes(d) && Array.isArray(v))
  return days.length ? (Object.fromEntries(days) as Record<string, number[][]>) : null
}

/**
 * Blind test r1 (Sam): "Monthly public Star Party at Griffith Observatory" counted as any day and got a 7:30 PM slot.
 * A place whose words say it only happens on certain dates (monthly, weekly, first Friday, every 2nd Saturday,
 * select nights, on set dates...) never fits a day on its own and never goes into Fill: we can't know the dates.
 */
const ORD = '(?:first|second|third|fourth|last|1st|2nd|3rd|4th|5th)'
const DOW = '(?:mon|tues?|wed(?:nes)?|thu(?:rs)?|fri|sat(?:ur)?|sun)(?:day)?s?'
const SET_DATES = new RegExp([
  '\\b(?:monthly|bi-?weekly|weekly|fortnightly|quarterly|annual(?:ly)?|yearly|seasonal(?:ly)?)\\b',
  `\\b${ORD}\\s+(?:and\\s+${ORD}\\s+)?${DOW}\\b`,
  `\\bevery\\s+(?:other\\s+|${ORD}\\s+)?(?:${DOW}|week|month|weekend)\\b`,
  '\\b(?:select|selected|certain|some|specific)\\s+(?:nights|dates|days|evenings|weekends|times)\\b',
  '\\bon\\s+(?:set|select|certain|specific)\\s+dates\\b',
  '\\b(?:once|twice)\\s+a\\s+(?:week|month|year)\\b',
  '\\bby\\s+(?:appointment|reservation)\\s+only\\b',
  // blind test r3 (Bree): a fair, festival, swap meet, flea or craft market, or a one-time / seasonal thing stored without
  // a date has dates we can't see
  '\\b(?:fairs?|festivals?|fests?|swap\\s+meets?|flea\\s+markets?|crafts?\\s+(?:markets?|fairs?|shows?)|bazaars?|one[-\\s]time|one\\s+night\\s+only|seasonal|pop[-\\s]?ups?)\\b',
].join('|'), 'i')
/** Only on certain dates we can't see (a place, not a dated event, and no date written in its own text). */
export function setDates(it: FitItem): boolean {
  if (it.kind === 'event' && it.start_at) return false
  if (textDates(it)) return false
  // journey test J3 (10/08): "Weekly open-air farmers market" with Sunday 9-2 hours is not a mystery date; a weekly cadence
  // plus known opening hours says exactly which days it runs. Any other set-dates cue still counts.
  if (knownHours(it)) return SET_DATES.test(words(it).replace(WEEKLY_CADENCE, ' '))
  return SET_DATES.test(words(it))
}
const WEEKLY_CADENCE = new RegExp(`\\b(?:weekly|every\\s+(?:${DOW}|week|weekend))\\b`, 'gi')
const words = (it: FitItem) => [it.title, it.one_line, it.schedule_text, it.hours_text].filter(Boolean).join(' · ')

/**
 * Blind test r1 (Tom): "Dia de los Muertos festival at MOLAA" is stored as a place, but its hours read "Sun Oct 25, 2026
 * from 11am". A place whose own text names calendar dates fits only those dates; with one date and a time, that time is
 * where the day puts it (never a made-up evening slot).
 */
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
const DATE_RX = /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+(\d{1,2})(?:st|nd|rd|th)?\b(?:,?\s+(20\d\d))?/gi
const TIME_RX = /\b(?:from|at|starts?(?: at)?|doors(?: at)?)?\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i
const ROLL_BACK_DAYS = 90
export function textDates(it: FitItem, now = new Date(), tz?: string | null): { days: string[]; hour: number | null } | null {
  if (it.kind === 'event' && it.start_at) return null
  const text = [it.hours_text, it.schedule_text, it.one_line, it.title].filter(Boolean).join(' · ')
  const today = ymdIn(now, tz)
  const days = new Set<string>()
  for (const m of text.matchAll(DATE_RX)) {
    const mo = MONTHS.indexOf(m[1].slice(0, 3).toLowerCase()) + 1, d = Number(m[2])
    if (!mo || d < 1 || d > 31) continue
    let y = m[3] ? Number(m[3]) : Number(today.slice(0, 4))
    let ymd = `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    // B-117 (R-106): a date with no year rolls to next year only when it is long gone ("Jan 5" read in October). A date a
    // few weeks back is a past date, not next year's: "Oct 3" read on Oct 9 read as Sun 10/3/2027 next to Sat 10/10.
    if (!m[3] && ymd < plusDays(today, -ROLL_BACK_DAYS)) { y += 1; ymd = `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}` }
    days.add(ymd)
  }
  if (!days.size) return null
  const t = days.size === 1 ? TIME_RX.exec(text) : null
  const hour = t ? (Number(t[1]) % 12) + (t[3].toLowerCase() === 'pm' ? 12 : 0) + (t[2] ? Number(t[2]) / 60 : 0) : null
  return { days: [...days].sort(), hour }
}
/** The instant of a wall-clock hour on a day in a zone (two passes, so a clock change settles). */
export function wallTime(ymd: string, hour: number, tz: string | null | undefined): string {
  const zone = tz || 'America/Los_Angeles'
  const [y, m, d] = ymd.split('-').map(Number)
  const h = Math.floor(hour), mi = Math.round((hour - h) * 60)
  const off = (at: number) => {
    const o: Record<string, number> = {}
    for (const x of new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }).formatToParts(new Date(at)))
      if (x.type !== 'literal') o[x.type] = Number(x.value)
    return Date.UTC(o.year, o.month - 1, o.day, o.hour % 24, o.minute) - at
  }
  const guess = Date.UTC(y, m - 1, d, h, mi)
  let at = guess - off(guess)
  at = guess - off(at)
  return new Date(at).toISOString()
}

/** Does an event have its own fixed day (not a weeks-long run)? */
export function fixedDay(it: FitItem, tz?: string | null): string | null {
  if (it.kind !== 'event' || !it.start_at) return null
  const long = it.end_at && new Date(it.end_at).getTime() - new Date(it.start_at).getTime() > RUN_MS
  return long ? null : ymdIn(it.start_at, tz)
}

/**
 * Does this saved thing fit that day? An event: on its day (a run: any day it runs, on its open weekdays). A place:
 * open that weekday when its hours are known; any day when they are not. Never a past day, never a gone thing.
 */
export function fitsDay(e: FitEntry, ymd: string, now = new Date()): boolean {
  return dayFit(e, ymd, now) === null
}
/** Why a saved thing does not fit a day (null = it fits). The day builder shows it greyed with this reason instead of
 *  hiding it (Alvin 10/15, pin 583), and the person may add it anyway. */
export type DayMiss =
  | { code: 'gone' } | { code: 'past' }
  | { code: 'other_day'; date: string }            // a dated event on another day
  | { code: 'not_running' }                         // a run (exhibition) that is not on that day
  | { code: 'closed'; dow: number }                 // closed that weekday (its hours, or "closed Mondays")
  | { code: 'only_weekdays'; days: number[] }       // its own words name its weekdays ("Friday Night Throwdown")
  | { code: 'only_dates'; dates: string[] }         // dates written in its own text
  | { code: 'set_dates' }                           // only on dates we can't see (monthly, a fair)
export function dayFit(e: FitEntry, ymd: string, now = new Date()): DayMiss | null {
  if (e.gone) return { code: 'gone' }
  if (ymd < ymdIn(now, e.tz)) return { code: 'past' }
  const it = e.item
  const hours = usableHours(it)
  const dow = dowOfYmd(ymd)
  const openThatDay = !hours || (hours[DAY_KEYS[dow]] ?? []).length > 0
  if (it.kind === 'event' && it.start_at) {
    const one = fixedDay(it, e.tz)
    if (one) return one === ymd && (!it.end_at || new Date(it.end_at).getTime() > now.getTime()) ? null : one === ymd ? { code: 'past' } : { code: 'other_day', date: one }
    const from = ymdIn(it.start_at, e.tz), to = it.end_at ? ymdIn(it.end_at, e.tz) : from
    if (ymd < from || ymd > to) return { code: 'not_running' }
    return openThatDay ? null : { code: 'closed', dow }
  }
  const named = textDates(it, now, e.tz)
  if (named) return named.days.includes(ymd) ? null : { code: 'only_dates', dates: named.days }
  if (setDates(it)) return { code: 'set_dates' }
  if (closedDays(it).includes(dow)) return { code: 'closed', dow }
  const wd = ownWeekdays(it)
  // "Friday Night Throwdown" is never on a Sunday, whatever its hours say; but weekdays its own hours say it is CLOSED
  // ("open daily, Tuesdays held for private bookings", B-133) are not the days it runs: then the hours decide
  const wdUse = wd && (!hours || wd.some((d) => (hours[DAY_KEYS[d]] ?? []).length > 0)) ? wd : null
  if (wdUse && !wdUse.includes(dow)) return { code: 'only_weekdays', days: wdUse }
  return openThatDay ? null : { code: 'closed', dow }
}
/** B-134: weekdays its own hours text or description says it is closed ("closed Mon.", "closed on Mondays"), for places
 *  whose hours we could not read (Stoneview: seasonal hours, "closed Mon."). */
const CLOSED_RX = /\bclosed\s+(?:on\s+)?(sun|mon|tue|wed|thu|fri|sat)[a-z]*\.?(?:\s*(?:and|&|,)\s*(sun|mon|tue|wed|thu|fri|sat)[a-z]*)?/gi
export function closedDays(it: FitItem): number[] {
  const out = new Set<number>()
  for (const m of `${it.hours_text ?? ''} · ${it.one_line ?? ''}`.matchAll(CLOSED_RX)) for (const g of [m[1], m[2]]) if (g) out.add(DAY_KEYS.indexOf(g.slice(0, 3).toLowerCase()))
  return [...out].filter((d) => d >= 0)
}
/**
 * A place whose own words name the weekday it happens ("Sunday noon bluegrass jam", "Saturdays at the market") fits only
 * those weekdays when we have no opening hours (blind test r1 round two: a Sunday jam was put on a Saturday).
 */
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
export function textWeekdays(it: FitItem): number[] | null {
  if (it.kind === 'event' && it.start_at) return null
  return weekdaysIn([it.title, it.one_line, it.schedule_text])
}
function weekdaysIn(parts: (string | null | undefined)[]): number[] | null {
  const t = parts.filter(Boolean).join(' ').toLowerCase()
  const out = WEEKDAYS.map((d, i) => (new RegExp(`\\b${d}s?\\b`).test(t) ? i : -1)).filter((i) => i >= 0)
  return out.length && out.length < 7 ? out : null
}
/**
 * The weekdays that limit when a place runs. With opening hours we trust, only a weekday in its NAME does ("Friday Night
 * Throwdown"); one in its description is an aside (blind round 2, Sam: a garden "open free seven days a week, with free
 * guided tours on some Saturdays" fitted only Saturdays). Without hours, its words decide (a Sunday jam, B-133 rule kept).
 */
export function ownWeekdays(it: FitItem): number[] | null {
  if (it.kind === 'event' && it.start_at) return null
  return usableHours(it) ? weekdaysIn([it.title]) : textWeekdays(it)
}
/** Runs any day (no day of its own, no hours that rule days out, not a set-dates thing). */
export const anyDay = (it: FitItem) => it.kind !== 'event' && !usableHours(it) && !setDates(it) && !textDates(it) && !textWeekdays(it) && !closedDays(it).length
/** No opening hours we trust: a time we put it at is a suggestion, so the card says "Check opening hours". */
export const hoursUnknown = (it: FitItem) => !(it.kind === 'event' && it.start_at) && !knownHours(it) && textDates(it)?.hour == null


/** Browse's When filter (QA r2: "First Thursday ArtWalk" under This weekend): may this place show for the dates
 *  from..to? A place with dates in its own text: only if one is inside; a set-dates place: never (its dates are unknown);
 *  a place naming its weekday: only if that weekday is inside. Everything else: yes (hours are checked separately). */
export function placeInRange(it: FitItem, from: string, to: string, now = new Date(), tz?: string | null): boolean {
  if (it.kind === 'event' && it.start_at) return true
  const t = textDates(it, now, tz)
  if (t) return t.days.some((d) => d >= from && d <= to)
  if (setDates(it)) return false
  const wd = ownWeekdays(it)
  if (wd) {
    for (let d = from, i = 0; d <= to && i < 8; d = plusDays(d, 1), i++) if (wd.includes(dowOfYmd(d))) return true
    return false
  }
  return true
}

// ---------- distance, local time, and settling a built day (Plan v3 blind test r2) ----------
/** km between two points (same formula as the server). */
export function kmBetween(a: { lat: number | null; lng: number | null }, b: { lat: number | null; lng: number | null }): number | null {
  if (a.lat == null || a.lng == null || b.lat == null || b.lng == null) return null
  const R = 6371, rad = Math.PI / 180
  const dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(x))
}
/** About how many minutes by car (the server's estimate: plan legBy). */
export const driveMin = (km: number) => (km <= 1 ? Math.max(2, Math.round((km * 1.3) / 5 * 60)) : Math.round((km <= 5 ? km * 3 : 15 + (km - 5) * 1.4) + 10))


const hmCache = new Map<string, Intl.DateTimeFormat>()
/** Decimal hour of an instant in a city's zone (19.5 = 7:30 PM). */
export function hourIn(at: number | string, tz: string | null | undefined): number {
  const zone = tz || 'America/Los_Angeles'
  let f = hmCache.get(zone)
  if (!f) { f = new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }); hmCache.set(zone, f) }
  const [h, m] = f.format(new Date(at)).split(':').map(Number)
  return h + m / 60
}
const Q = 15 * 60e3
const upQuarter = (t: number) => Math.ceil(t / Q) * Q
/** B-089: how soon a stop on today's day can start: 30 minutes from now, up to the next quarter hour. The server's
 *  floor (plan placeItem: no stop is placed before it); settleDay only keeps stops out of the past. */
export const SOON_MIN = 30
export const soonestStart = (now: Date | number) => upQuarter(new Date(now).getTime() + SOON_MIN * 60e3)
/** Nothing is put on a day before this unless it has a real fixed start (blind test r2, Gabe: board games at 8 AM). */
export const DAY_START_HOUR = 9
/**
 * The part of day a place's own words name (blind test r2, Gabe: "Board game afternoon" at 8 AM): [earliest, preferred
 * start, latest start] in decimal hours, or null.
 */
export function partOfDay(it: { title?: string; one_line?: string | null }): [number, number, number] | null {
  const t = `${it.title ?? ''} ${it.one_line ?? ''}`.toLowerCase()
  if (/\bbrunch\b/.test(t)) return [10, 10.5, 12.5]
  if (/\bmornings?\b|\bsunrise\b/.test(t)) return [DAY_START_HOUR, 9.5, 11.5]
  if (/\bafternoons?\b|\bmatinee\b/.test(t)) return [12, 13, 16.5]
  if (/\bsunset\b|\bgolden hour\b/.test(t)) return [17, 17.5, 19]
  if (/\bevenings?\b/.test(t)) return [17, 18, 21]
  if (/\bnights?\b|\bnightlife\b|\bafter dark\b|\blate[- ]night\b/.test(t)) return [19, 20, 22.5]
  return null
}
/**
 * B-144 (10/15): the part of day a thing belongs in, so a built day never puts a patio dinner at 10 AM or late drinks at
 * 3:45 PM. [earliest start, preferred start, latest start] in decimal hours (latest may pass midnight: 23 = 11 PM), or null
 * when any daytime hour is fine. Its own words first (partOfDay: brunch, morning, afternoon, sunset, evening, night), then
 * dinner / breakfast words, then its kind (bars and late-night things in the evening, food at lunch or dinner, never
 * breakfast time), then how late it runs (vibe lateness). A soft rule: the day builder prefers it, the person can still pick
 * any time (Change time), and opening hours always come first.
 */
export type DaypartItem = { title?: string; one_line?: string | null; category?: string | null; tags?: string[] | null; vibe?: { dims?: Record<string, number> } | null }
export function daypart(it: DaypartItem | null | undefined): [number, number, number] | null {
  if (!it) return null
  const words = partOfDay(it)
  if (words) return words
  const t = `${it.title ?? ''} ${it.one_line ?? ''}`
  const late = it.vibe?.dims?.lateness
  const tags = it.tags ?? []
  // a market goes when it opens (its hours, or the morning), whatever it sells
  if (it.category === 'market' || /\b(?:farmers'?|flea|night)\s+markets?\b/i.test(t)) return /\bnight\s+markets?\b/i.test(t) ? [17, 18.5, 21.5] : null
  if (/\b(?:dinner|supper)\b/i.test(t)) return [17, 19, 21.5]
  if (/\b(?:breakfast)\b/i.test(t)) return [8, 9.5, 11.5]
  if (it.category === 'nightlife' || tags.includes('late-night') || /\blate(?:-night)? (?:drinks?|bites?|show)\b/i.test(t)) return [19, 20.5, 23]
  if (it.category === 'drink') return typeof late === 'number' && late >= 0.6 ? [19, 20.5, 23] : [16, 19, 22.5]
  if (it.category === 'food') return [11, 12.5, 21.5]
  if (typeof late === 'number' && late >= 0.6) return [17, 19.5, 23]
  return null
}
/** "After the show": a daypart that starts in the evening (the builder may put it after an evening event, never before noon). */
export const eveningPart = (it: DaypartItem | null | undefined) => (daypart(it)?.[0] ?? 0) >= 15
/** A fixed-time event (its own day and a real start, not a date without a time). */
export function isFixed(it: FitItem | null | undefined, tz: string | null): boolean {
  return Boolean(it && it.kind === 'event' && it.start_at && fixedDay(it, tz) && hourIn(it.start_at, tz) !== 0)
}


/** The fields the drop-in rule reads (an Item on the server, a saved thing's item in the app). */
export type DropInItem = { kind?: string | null; start_at?: string | null; end_at?: string | null; category?: string | null; vibe?: { dims?: Record<string, number> } | null }
/** A dated event that starts at exactly 00:00 in the zone and lasts a day at most: a date without a time. */
export function dateOnlyIn(it: DropInItem, tz: string | null | undefined): boolean {
  if (it.kind !== 'event' || !it.start_at) return false
  const s = new Date(it.start_at).getTime()
  if (it.end_at && new Date(it.end_at).getTime() - s > 24 * 3600e3 + 60e3) return false // a run, timed inside the run
  return hourIn(s, tz) === 0
}
const DROP_IN_CATS = new Set(['art', 'market', 'community', 'outdoors'])
/**
 * THE drop-in rule, used by the server (rank.ts isDropIn: which moves plan accepts) and the app (settleDay: which moves it
 * proposes). A dated thing you drop into (open studio, market, festival day): 3.5-12 hours long, not a date without a time,
 * and a drop-in kind (art, market, community, outdoors, or loosely structured). Anything else with a start is a set time.
 * B-095: settleDay treated every 4-hour event as a window, the server did not, and refused the moves with 400.
 */
export function dropInWindow(it: DropInItem | null | undefined, tz: string | null | undefined): boolean {
  if (!it || it.kind !== 'event' || !it.start_at || !it.end_at || dateOnlyIn(it, tz)) return false
  const d = new Date(it.end_at).getTime() - new Date(it.start_at).getTime()
  return d >= 3.5 * 3600e3 && d <= 12 * 3600e3 && (DROP_IN_CATS.has(it.category ?? '') || (it.vibe?.dims?.structure ?? 1) <= 0.2)
}
/** The shortest visit the server accepts before closing time or the end of a drop-in window (plan/index.ts MIN_VISIT). */
export const MIN_VISIT_MS = 40 * 60e3

/**
 * B-089: a thing that can no longer go on TODAY's day; a day built for today leaves it out with a reason (plan/placing.ts
 * placeItem, TOO_LATE_TODAY / ALREADY_STARTED). A set-time event that already started; a window or run that ends before
 * the soonest start plus a short visit; a date without a time after 4 PM (the server puts those between 10 and 5); a place
 * whose opening hours today all close before then; a place with unknown hours after the latest start of its part of day
 * (B-206: an afternoon jam at 10:30 PM), or after 10 PM when it has none. Any other day: false.
 * The day builder uses it so it never offers or ticks something the server would leave out (journey J3).
 */
export function lateToday(it: FitItem & DropInItem, ymd: string, tz: string | null, now: Date | number = Date.now()): boolean {
  const t = new Date(now).getTime()
  if (ymd !== ymdIn(new Date(t), tz)) return false
  const soon = soonestStart(t)
  if (ymdIn(new Date(soon), tz) !== ymd) return true
  if (it.kind === 'event' && it.start_at) {
    if (dateOnlyIn(it, tz)) return hourIn(soon, tz) > 16
    if (dropInWindow(it, tz) || !fixedDay(it, tz)) return it.end_at ? new Date(it.end_at).getTime() < soon + MIN_VISIT_MS : false
    return new Date(it.start_at).getTime() < t
  }
  const h = usableHours(it)
  if (!h) return hourIn(soon, tz) > (daypart(it as FitItem & DaypartItem)?.[2] ?? 22)
  return !(h[DAY_KEYS[dowOfYmd(ymd)]] ?? []).some(([, c]) => new Date(wallTime(ymd, c, tz)).getTime() >= soon + MIN_VISIT_MS)
}

/**
 * B-210: the first start that day the server takes for a place by its own hours (plan placing.ts timed: a visit of at
 * least MIN_VISIT before that opening's close, never before the soonest start today). Null when no opening fits, or the
 * hours are unknown; the day builder then keeps the place's left-out line instead of sending a time that gets a 400.
 */
export function openingStart(it: FitItem, ymd: string, tz: string | null, now: Date | number = Date.now()): string | null {
  const h = usableHours(it)
  if (!h) return null
  const soon = ymd === ymdIn(new Date(now), tz) ? soonestStart(now) : -Infinity
  for (const [o, c] of h[DAY_KEYS[dowOfYmd(ymd)]] ?? []) {
    const at = Math.max(new Date(wallTime(ymd, o, tz)).getTime(), soon)
    if (at + MIN_VISIT_MS <= new Date(wallTime(ymd, c, tz)).getTime()) return new Date(at).toISOString()
  }
  return null
}


export const legMs = (a: { lat: number | null; lng: number | null }, b: { lat: number | null; lng: number | null }) => {
  const k = kmBetween(a, b)
  return k === null ? 15 * 60e3 : driveMin(k) * 60e3
}
export type Settle = { moves: { id: string; start: string; end?: string }[]; clashes: Set<string> }
/**
 * Make a built day possible. Fixed-time events keep their start. Places between them are put in nearest-neighbour
 * order (less back-and-forth) and timed one after another with the drive between, never before 9 AM, before the place
 * opens, or before the part of day it belongs in (daypart: a dinner in the evening, a bar after 4 PM). A run of places that
 * would run into the next fixed event is pulled earlier when it can be; when it can't, the places that belong later go
 * after the event instead ("drinks after the show", B-144), and when that fails too the last place's visit is shortened
 * to end in time (B-131). Only when nothing works is the stop flagged ("not enough time to drive here"); nothing is dropped.
 */
export type SettleStop = { id: string; start: string | null; end: string | null; lat: number | null; lng: number | null; item: (FitItem & DaypartItem) | null }
/**
 * When a visit starting at `at` must end by: an event's end, else the closing time of the opening range the start falls in
 * (or, before opening, the first range that opens after it), else the end of the day when hours are unknown. Closed then
 * (trusted hours, no range that day contains or follows the start) is -Infinity. Mirrors the server's own check exactly
 * (times.ts closingAt, plan/index.ts timed()); B-095 review: split hours 9-12 / 6-10 PM must not take an 11:45 visit.
 */
/** B-211: the hours the settlers trust are the server's own (times.ts usableHours: structured hours when clear, else the
 *  place's hours text). Reading only structured hours, the app moved a place known from its text to close mid-afternoon to
 *  7 PM and the plan move came back 400 "This place closes before your visit starts". Null for an event or unknown hours. */
function settleHours(st: SettleStop): Record<string, number[][]> | null {
  const it = st.item as (FitItem & { kind?: string; start_at?: string | null }) | null
  if (!it || (it.kind === 'event' && it.start_at)) return null
  return usableHours(it)
}
function closeOf(st: SettleStop, at: number, tz: string | null): number {
  const ev = st.item as { kind?: string; end_at?: string | null } | null
  if (ev && ev.kind === 'event' && ev.end_at) return new Date(ev.end_at).getTime()
  const h = settleHours(st)
  const day = ymdIn(new Date(at), tz)
  if (!h) return new Date(wallTime(day, 23.5, tz)).getTime()
  const hour = hourIn(at, tz), dow = dowOfYmd(day)
  const ranges = (k: number) => { const r = h[DAY_KEYS[k]]; return Array.isArray(r) ? (r as number[][]) : [] }
  let close: number | null = ranges(dow).find(([o, c]) => o <= hour + 1e-6 && hour < c)?.[1] ?? null
  if (close == null && hour < 6) { const y = ranges((dow + 6) % 7).find(([o, c]) => o <= hour + 24 && hour + 24 < c); if (y) close = y[1] - 24 }
  if (close == null) close = ranges(dow).filter(([o]) => o > hour).map(([, c]) => c)[0] ?? null
  return close == null ? -Infinity : new Date(wallTime(day, close, tz)).getTime()
}
/** B-144: is the place open at `at` (trusted hours only; an event, or unknown hours, always is)? A bar that opens at 4 PM is
 *  never pulled to 2 PM to make room before a show (June, 10/15: Bar Covell at 2:00 PM). */
function openAt(st: SettleStop, at: number, tz: string | null): boolean {
  const h = settleHours(st)
  if (!h) return true
  const day = ymdIn(new Date(at), tz), hour = hourIn(at, tz), dow = dowOfYmd(day)
  const ranges = (k: number) => { const r = h[DAY_KEYS[k]]; return Array.isArray(r) ? (r as number[][]) : [] }
  return ranges(dow).some(([o, c]) => o <= hour + 1e-6 && hour < c) || (hour < 6 && ranges((dow + 6) % 7).some(([o, c]) => o <= hour + 24 && hour + 24 < c))
}
/** The first opening hour that day (trusted hours), or null. */
function opensOn(st: SettleStop, ymd: string): number | null {
  const r = settleHours(st)?.[DAY_KEYS[dowOfYmd(ymd)]]
  return Array.isArray(r) && r.length ? Math.min(...(r as number[][]).map(([o]) => o)) : null
}
/** The last closing hour that day (trusted hours), or Infinity. */
function closesBy(st: SettleStop, ymd: string): number {
  const r = settleHours(st)?.[DAY_KEYS[dowOfYmd(ymd)]]
  return Array.isArray(r) && r.length ? Math.max(...(r as number[][]).map(([, c]) => c)) : Infinity
}
/** A move the server accepts (plan/index.ts timed()): at least 40 minutes before closing (or the window's end), never
 *  before a window opens, and (B-144) only while the place is open. Shared by settleDay and settleInOrder. */
function canStartAt(s: SettleStop, at: number, tz: string | null): boolean {
  return at + MIN_VISIT_MS <= closeOf(s, at, tz) && openAt(s, at, tz) && inEntryWindow(s, at, tz) && (!dropInWindow(s.item as DropInItem | null, tz) || at >= new Date((s.item as { start_at: string }).start_at).getTime())
}
/**
 * Timed entry (B-201): a place that only admits arrivals in windows (Lake Shrine: 10-11:30 AM, 1-3 PM) keeps its day's opening
 * range in items.hours and lists the windows in hours.entry, same weekly shape. An arrival must fall inside a window (its last
 * minute included); the visit itself runs to the day's closing, so the 40-minute rule never applies to a window's end.
 * No entry list for that day = no limit.
 */
function inEntryWindow(s: SettleStop, at: number, tz: string | null): boolean {
  const it = s.item as { kind?: string; start_at?: string | null; hours?: (Record<string, unknown> & { unclear?: boolean; entry?: Record<string, unknown> }) | null } | null
  const e = it && !(it.kind === 'event' && it.start_at) && it.hours && !it.hours.unclear ? it.hours.entry : null
  if (!e || typeof e !== 'object') return true
  const day = ymdIn(new Date(at), tz), r = e[DAY_KEYS[dowOfYmd(day)]]
  if (!Array.isArray(r) || !r.length) return true
  const hour = hourIn(at, tz)
  return (r as number[][]).some(([o, c]) => o <= hour + 1e-6 && hour <= c + 1e-6)
}
/** The earliest a place may start that day: 9 AM (or `soon`, the soonest start today), when it opens, a window's opening,
 *  and its part of day. Shared by settleDay and settleInOrder. */
function startFloor(s: SettleStop, ymd: string, tz: string | null, soon: number): number {
  const isWindow = dropInWindow(s.item as DropInItem | null, tz)
  const dayStart = Math.max(new Date(wallTime(ymd, DAY_START_HOUR, tz)).getTime(), soon)
  const part = s.item ? daypart(s.item) : null
  const opens = isWindow ? new Date((s.item as { start_at: string }).start_at).getTime() : -Infinity
  const oh = opensOn(s, ymd)
  // the 9 AM floor gives way to a place that opens earlier (blind round 2, dev: a lesson open 8:20-9:20 was pushed to 9:00,
  // then flagged); its own opening is the floor then
  // only for a place that cannot fit a visit after 9 AM that day (a market open 8-4 still starts the day at 9)
  const early = oh != null && oh < DAY_START_HOUR && closesBy(s, ymd) < DAY_START_HOUR + MIN_VISIT_MS / 3600e3
  const floor = early ? Math.max(new Date(wallTime(ymd, oh, tz)).getTime(), soon) : dayStart
  const partFrom = part ? (early ? Math.min(part[0], oh) : part[0]) : null
  return Math.max(floor, partFrom != null ? new Date(wallTime(ymd, partFrom, tz)).getTime() : floor, opens, oh != null ? new Date(wallTime(ymd, oh, tz)).getTime() : -Infinity)
}
/** The shortest a place visit is cut to so it ends before the next fixed event (B-131), instead of a clash. */
export const SHRINK_MIN_MS = 45 * 60e3
/** `now` (the app passes it; tests may leave it out): a stop already under way or over stays where it is, and nothing is
 *  moved into the past (B-089: a day settled at 4 PM never pulls a stop back to 10 AM). */
export function settleDay(stops: SettleStop[], ymd: string, tz: string | null, pinned: Set<string> = new Set(), now?: Date | number): Settle {
  if (!stops.length || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return { moves: [], clashes: new Set() } // nothing loaded yet
  const timed = stops.filter((s) => s.start).sort((a, b) => new Date(a.start!).getTime() - new Date(b.start!).getTime())
  // a stop the person timed themselves (Change time) stays where they put it, like a fixed event
  // a drop-in window (a craft show 10 AM-4 PM, 4 hours or more) is not a fixed time: the visit moves inside the window
  // (journey J2, 10/08: a morning market ran into the show's opening minute)
  // which events count as windows is the server's own rule (dropInWindow), or the server refuses the move (B-095)
  const isWindow = (s: SettleStop) => dropInWindow(s.item as DropInItem | null, tz)
  const nowMs = now == null ? -Infinity : new Date(now).getTime()
  // the floor is the next quarter hour, not now + 30: the server already kept the 30 minutes when it placed the stop, and a
  // floor that moves with the clock would push a 4:30 stop to 4:45 a minute after the day was built
  const soon = now == null ? -Infinity : upQuarter(nowMs)
  const begun = (s: SettleStop) => new Date(s.start!).getTime() < nowMs
  const fixed = (s: SettleStop) => (isFixed(s.item, tz) && !isWindow(s)) || pinned.has(s.id) || begun(s)
  const dur = (s: SettleStop) => (s.end ? new Date(s.end).getTime() - new Date(s.start!).getTime() : 2 * 3600e3)
  // a move the server accepts (plan/index.ts timed()): at least 40 minutes before closing (or the window's end), never
  // before a window opens, and (B-144) only while the place is open. A stop that can't go where the day would put it stays
  // where it is and is flagged (B-095).
  const canStart = (s: SettleStop, at: number) => canStartAt(s, at, tz)
  // the earliest a place may start: 9 AM (or the soonest start today), when it opens, a window's opening, and its part of day
  const lower = (s: SettleStop) => startFloor(s, ymd, tz, soon)
  const out: Settle = { moves: [], clashes: new Set() }
  type Placed = { s: SettleStop; start: number; end: number; cut?: boolean }
  const placed: Placed[] = []
  /** Nearest-neighbour order from where the day already is (or from the earliest place). */
  const nearestOrder = (run: SettleStop[], from0: SettleStop | null) => {
    const order: SettleStop[] = []
    const left = [...run]
    let from = from0
    while (left.length) {
      let k = 0
      if (from) { let best = Infinity; left.forEach((x, j) => { const d = legMs(from!, x); if (d < best) { best = d; k = j } }) }
      from = left.splice(k, 1)[0]
      order.push(from)
    }
    return order
  }
  /** One after another from where the day is, each no earlier than it may start (the first keeps its own later start). */
  const forward = (order: SettleStop[], prev: Placed | null): Placed[] => {
    let cursor = prev ? prev.end : -Infinity
    let last: SettleStop | null = prev?.s ?? null
    const times: Placed[] = []
    for (const s of order) {
      const at = upQuarter(Math.max(lower(s), last ? cursor + legMs(last, s) : -Infinity, prev || times.length ? -Infinity : new Date(s.start!).getTime()))
      times.push({ s, start: at, end: at + dur(s) })
      cursor = at + dur(s); last = s
    }
    return times
  }
  const emit = (times: Placed[]) => {
    for (const t of times) {
      const was = new Date(t.s.start!).getTime()
      if (Math.abs(t.start - was) >= 60e3 || t.cut) {
        if (canStart(t.s, t.start)) out.moves.push({ id: t.s.id, start: new Date(t.start).toISOString(), ...(t.cut ? { end: new Date(t.end).toISOString() } : {}) })
        else { out.clashes.add(t.s.id); placed.push({ s: t.s, start: was, end: was + dur(t.s) }); continue }
      }
      placed.push(t)
    }
  }
  let i = 0
  while (i < timed.length) {
    // a run of places up to the next fixed event
    const run: SettleStop[] = []
    while (i < timed.length && !fixed(timed[i])) run.push(timed[i++])
    const nextFixed = i < timed.length ? timed[i] : null
    // B-208: the run follows the stop that ends last so far (a 12-4 class), not the one placed last (a 1 PM stop inside it)
    const prev = placed.reduce<Placed | null>((a, p) => (!a || p.end > a.end ? p : a), null)
    const order = nearestOrder(run, prev?.s ?? null)
    let times = forward(order, prev)
    if (nextFixed) {
      const fs = new Date(nextFixed.start!).getTime()
      const fixedEnd = fs + dur(nextFixed)
      const overOf = (ts: Placed[]) => (ts.length ? ts[ts.length - 1].end + legMs(ts[ts.length - 1].s, nextFixed) - fs : prev ? prev.end + legMs(prev.s, nextFixed) - fs : 0)
      /** These places, before the fixed event: as timed, or pulled earlier when every one of them may start that much sooner. */
      const before = (ts: Placed[]): Placed[] | null => {
        const over = overOf(ts)
        if (over <= 0 || !ts.length) return over <= 0 ? ts : null
        const shift = Math.ceil(over / Q) * Q
        const floor = prev ? prev.end : -Infinity
        const ok = ts.every((t, j) => t.start - shift >= lower(t.s) && canStart(t.s, t.start - shift) && (j > 0 || t.start - shift >= floor + (prev ? legMs(prev.s, t.s) : 0)))
        return ok ? ts.map((t) => ({ ...t, start: t.start - shift, end: t.end - shift })) : null
      }
      /** These places, after the fixed event (journey J2: a morning market; B-144: drinks after the show), when each one can
       *  still start there (open, 40 minutes before closing) and the next fixed thing after it is still reachable. */
      const after = (ss: SettleStop[]): Placed[] | null => {
        let at = fixedEnd, lastS: SettleStop = nextFixed
        const res: Placed[] = []
        for (const s of ss) {
          const st = upQuarter(Math.max(at + legMs(lastS, s), lower(s)))
          if (!canStart(s, st)) return null
          const en = Math.min(st + dur(s), closeOf(s, st, tz))
          res.push({ s, start: st, end: en }); at = en; lastS = s
        }
        const following = i + 1 < timed.length && fixed(timed[i + 1]) ? timed[i + 1] : null
        if (following && res.length && res[res.length - 1].end + legMs(res[res.length - 1].s, following) > new Date(following.start!).getTime()) return null
        return res
      }
      const fit = before(times)
      if (fit) times = fit
      else if (!times.length) out.clashes.add(nextFixed.id) // two fixed things too close together
      else {
        // B-144: move the places that belong latest in the day to after the event, as few as possible (a bar opening at 4
        // and late drinks before an 8 PM show: the drinks go after the show, the bar stays before it)
        const byLate = [...order].sort((a, b) => lower(b) - lower(a))
        let done = false
        for (let k = 1; k <= order.length && !done; k++) {
          const go = new Set(byLate.slice(0, k))
          const keep = order.filter((s) => !go.has(s)), later = order.filter((s) => go.has(s))
          const b = keep.length ? before(forward(keep, prev)) : (overOf([]) <= 0 ? [] : null)
          const a = b ? after(nearestOrder(later, nextFixed)) : null
          if (!b || !a) continue
          emit(b)
          placed.push({ s: nextFixed, start: fs, end: fixedEnd })
          emit(a)
          done = true
        }
        if (done) { i++; continue }
        // B-131: the last place's visit is cut short to end in time (not under 45 minutes), so the day stays possible
        const last = times[times.length - 1]
        const over = overOf(times)
        if (!isWindow(last.s) && last.end - over - last.start >= SHRINK_MIN_MS) times = [...times.slice(0, -1), { ...last, end: Math.floor((last.end - over) / Q) * Q, cut: true }]
        else out.clashes.add(nextFixed.id)
      }
    }
    emit(times)
    if (nextFixed) { const fs = new Date(nextFixed.start!).getTime(); placed.push({ s: nextFixed, start: fs, end: fs + dur(nextFixed) }); i++ }
  }
  return out
}


export type InOrderMiss = { code: 'closed' | 'before' | 'set'; other?: string }
export type InOrder = { moves: { id: string; start: string; end: string }[]; unfit: Map<string, InOrderMiss> }
/**
 * Pin 595 (Alvin 10/16): the person put the day's stops in this order (Move up / Move down / drag); time it that way.
 * Stops with their own time (an event, one the person set, one already under way) keep it; every other stop goes one after
 * another in the given order, each at the first quarter hour that is open, inside its part of day, and leaves the drive
 * from the stop before (the same rules settleDay and the server's move check use). A run of stops that would run into the
 * next set-time stop is pulled earlier when every stop in it can start that much sooner, else the last visit is cut short
 * (not under 45 minutes, B-131), else that stop keeps its old time and is reported ('before'). A stop that is closed for
 * the rest of the day keeps its old time ('closed'). Two set-time stops in the wrong order: nothing moves ('set').
 * Never re-orders. `anchor`: the earliest the first stop may start (default: the day's earliest start). Pure.
 */
export function settleInOrder(order: SettleStop[], ymd: string, tz: string | null, pinned: Set<string> = new Set(), now?: Date | number, anchor?: number): InOrder {
  const out: InOrder = { moves: [], unfit: new Map() }
  const stops = order.filter((s) => s.start)
  if (!stops.length || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return out
  const nowMs = now == null ? -Infinity : new Date(now).getTime()
  const soon = now == null ? -Infinity : upQuarter(nowMs)
  const isWindow = (s: SettleStop) => dropInWindow(s.item as DropInItem | null, tz)
  const fixed = (s: SettleStop) => (isFixed(s.item, tz) && !isWindow(s)) || pinned.has(s.id) || new Date(s.start!).getTime() < nowMs
  const was = (s: SettleStop) => new Date(s.start!).getTime()
  const dur = (s: SettleStop) => (s.end ? new Date(s.end).getTime() - was(s) : 2 * 3600e3)
  const set = stops.filter(fixed)
  for (let k = 1; k < set.length; k++) if (was(set[k]) < was(set[k - 1])) { out.unfit.set(set[k].id, { code: 'set', other: set[k - 1].id }); return out }
  const first = anchor ?? Math.min(...stops.map(was))
  const lastStart = new Date(wallTime(ymd, 23.5, tz)).getTime()
  type P = { s: SettleStop; start: number; end: number }
  const placed: P[] = []
  let run: P[] = [] // places since the last set-time stop
  let prevFixed: P | null = null
  /** The first start at or after `at` when it can be there (open, 40 minutes before closing), or null. */
  const openFrom = (s: SettleStop, at: number) => { for (let t = upQuarter(at); t <= lastStart; t += Q) if (canStartAt(s, t, tz)) return t; return null }
  const visit = (s: SettleStop, at: number): P => ({ s, start: at, end: Math.min(at + dur(s), Math.max(at + MIN_VISIT_MS, closeOf(s, at, tz))) })
  // B-208: the stop a flexible one follows is the one that ends LAST so far, not the one placed last: a time set inside a
  // long class (Mosaic at 1 PM in a 12-4 class) must not pull the next stop into the class
  const latest = () => placed.reduce<P | null>((a, p) => (!a || p.end > a.end ? p : a), null)
  for (const s of stops) {
    const last = latest()
    if (!fixed(s)) {
      const at = openFrom(s, Math.max(startFloor(s, ymd, tz, soon), last ? last.end + legMs(last.s, s) : first))
      // it cannot be open anywhere in the day: it keeps its time and says why, and still holds that time, so the stops after
      // it are placed after it, never on top of it (R4 gate 10/16: a closed Lake Shrine at noon pulled Getty Villa to noon)
      if (at == null) { out.unfit.set(s.id, { code: 'closed' }); placed.push({ s, start: was(s), end: was(s) + dur(s) }); continue }
      const v = visit(s, at)
      placed.push(v); run.push(v)
      continue
    }
    const fs = was(s)
    // the run before this set-time stop must end in time to get there
    for (;;) {
      const tail = run[run.length - 1]
      if (!tail) break
      const over = tail.end + legMs(tail.s, s) - fs
      if (over <= 0) break
      const shift = Math.ceil(over / Q) * Q
      const floor0 = prevFixed ? prevFixed.end : -Infinity
      const ok = run.every((t, j) => t.start - shift >= startFloor(t.s, ymd, tz, soon) && canStartAt(t.s, t.start - shift, tz) && (j > 0 || t.start - shift >= floor0 + (prevFixed ? legMs(prevFixed.s, t.s) : 0)))
      if (ok) { for (const t of run) { t.start -= shift; t.end -= shift } continue }
      const cutEnd = Math.floor((tail.end - over) / Q) * Q
      if (!isWindow(tail.s) && cutEnd - tail.start >= SHRINK_MIN_MS) { tail.end = cutEnd; continue }
      // it cannot go before the set-time stop: it keeps its old time, and says why
      out.unfit.set(tail.s.id, { code: 'before', other: s.id })
      run = run.slice(0, -1)
      placed.splice(placed.indexOf(tail), 1)
    }
    const f = { s, start: fs, end: fs + dur(s) }
    placed.push(f); if (!prevFixed || f.end > prevFixed.end) prevFixed = f; run = []
  }
  for (const p of placed) {
    if (fixed(p.s)) continue
    const endWas = p.s.end ? new Date(p.s.end).getTime() : was(p.s) + dur(p.s)
    if (Math.abs(p.start - was(p.s)) >= 60e3 || Math.abs(p.end - endWas) >= 60e3) out.moves.push({ id: p.s.id, start: new Date(p.start).toISOString(), end: new Date(p.end).toISOString() })
  }
  return out
}


// ---------- suggestions: which candidates round out a day (blind tests r2 + r3) ----------
export type DayShape = { start: number | null; end: number | null; lat: number | null; lng: number | null; venue: string | null; title: string }
export type SugItem = FitItem & { id: string; title: string; lat: number | null; lng: number | null; venue_name: string | null; one_line?: string | null; category?: string }
const normName = (x: string | null | undefined) => (x ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
/** How far each widening step reaches (minutes' drive) and which window it uses (blind test r3, Dana: one-item days). */
export const SUGGEST_STEPS = [
  { near: 15, picksOnly: true, from: DAY_START_HOUR, to: 23, gapMin: 60 },
  { near: 25, picksOnly: true, from: DAY_START_HOUR, to: 23, gapMin: 60 },
  { near: 25, picksOnly: false, from: DAY_START_HOUR, to: 23, gapMin: 60 },
  { near: 25, picksOnly: false, from: 8, to: 23.5, gapMin: 45 },
] as const
/**
 * Pick up to n suggestions from ranked candidates (the person's For you order), widening step by step until there are
 * enough: their own categories within 15 min, then within 25 min, then any category within 25 min (tagged nearby), then a
 * wider window. Never a set-dates place, never the same venue or show already on the day, and only into a real gap
 * (events: their own time with the drive in and out; places: an open stretch long enough for a visit).
 */
export function pickSuggestions<T extends SugItem>(cands: T[], o: { ymd: string; tz: string | null; exclude: Set<string>; day: DayShape[]; n: number; isPick: (it: T) => boolean; now?: Date }): { item: T; kind: 'pick' | 'nearby'; step: number }[] {
  if (o.n <= 0) return []
  const pts = o.day.filter((x) => x.lat != null && x.lng != null) as (DayShape & { lat: number; lng: number })[]
  const venues = new Set(o.day.map((x) => normName(x.venue)).filter(Boolean))
  const titles = new Set(o.day.map((x) => normName(x.title)))
  const timed = o.day.filter((x) => x.start != null && x.end != null).sort((a, b) => a.start! - b.start!) as (DayShape & { start: number; end: number })[]
  const base = cands.filter((it) => {
    if (o.exclude.has(it.id) || setDates(it) || !fitsDay({ item: it, tz: o.tz }, o.ymd, o.now) || lateToday(it as SugItem & DropInItem, o.ymd, o.tz, o.now)) return false
    const v = normName(it.venue_name), ti = normName(it.title)
    if (pts.some((x) => { const k = kmBetween(x, it); return k !== null && k < 0.3 })) return false // same place (Bree: two names for one reserve)
    // the same show or venue under a longer or shorter name ("Davide De Pierro" vs "Davide De Pierro - Evening Show",
    // "El Rey Theatre" vs "El Rey Theatre - Los Angeles"): one name inside the other is the same thing (preview walk r3)
    if ([...titles].some((t) => t.length > 8 && ti.length > 8 && (ti.includes(t) || t.includes(ti)))) return false
    if (v && [...venues].some((x) => x.length > 3 && (v.startsWith(x) || x.startsWith(v)))) return false
    return !((v && venues.has(v)) || titles.has(ti) || [...venues].some((x) => x.length > 3 && ti.includes(x)))
  })
  const out: { item: T; kind: 'pick' | 'nearby'; step: number }[] = []
  const usedIds = new Set<string>(), usedVenues = new Set<string>()
  SUGGEST_STEPS.forEach((st, step) => {
    if (out.length >= o.n) return
    // B-089: on today, the open stretch starts at the soonest start (a morning gap that has passed is not a gap)
    const winStart = Math.max(new Date(wallTime(o.ymd, st.from, o.tz)).getTime(), o.ymd === ymdIn(o.now ?? new Date(), o.tz) ? soonestStart(o.now ?? new Date()) : -Infinity), winEnd = new Date(wallTime(o.ymd, Math.min(st.to, 21.5 + (st.to - 23)), o.tz)).getTime()
    const fitsGap = (it: T) => {
      if (isFixed(it, o.tz)) {
        const s = new Date(it.start_at!).getTime(), e = it.end_at ? new Date(it.end_at).getTime() : s + 2 * 3600e3
        const h = hourIn(s, o.tz)
        if (h < st.from || h >= st.to) return false
        return timed.every((x) => (x.end + legMs(x, it) <= s) || (e + legMs(it, x) <= x.start))
      }
      const edges = [{ end: winStart, lat: null, lng: null } as { end: number; lat: number | null; lng: number | null }, ...timed]
      for (let j = 0; j < edges.length; j++) {
        const from = edges[j], to = timed[j] ?? null
        const free = (to ? to.start : winEnd) - Math.max(from.end, winStart) - (j ? legMs(from, it) : 0) - (to ? legMs(it, to) : 0)
        if (free >= st.gapMin * 60e3) return true
      }
      return false
    }
    const near = (it: T) => !pts.length || pts.some((x) => { const k = kmBetween(x, it); return k === null || driveMin(k) <= st.near })
    for (const it of base) {
      if (out.length >= o.n) break
      const pick = o.isPick(it)
      if (usedIds.has(it.id) || (st.picksOnly && !pick) || !near(it) || !fitsGap(it)) continue
      const v = normName(it.venue_name) || normName(it.title)
      if (usedVenues.has(v)) continue
      usedIds.add(it.id); usedVenues.add(v)
      out.push({ item: it, kind: pick ? 'pick' : 'nearby', step })
    }
  })
  return out
}

/**
 * When a place with no stated time should start (blind test r3, Dana: a garden festival at 5:30 PM): evening things
 * (nightlife, music, comedy, film, theater, drinks, or words that say evening or night) are left to the server; everything
 * else starts in the day, at its opening hour that weekday when known (10 AM at the earliest), else 10 AM, and never after
 * 4 PM. Returns a decimal hour or null (let the server choose).
 */
const EVENING_CATS = new Set(['nightlife', 'music', 'comedy', 'film', 'theater', 'drink'])
/**
 * A guessed hour (part of day, daytime default) moved inside the place's trusted opening hours that day: kept when a 40-minute
 * visit fits where it is, else the nearest opening that leaves 40 minutes (blind round 2, dev: a "Saturday-morning" lesson
 * open 8:20-9:20 was guessed at 10 AM and the server left it out of the day). No trusted hours, or closed that day: unchanged.
 */
export function guessInHours(it: FitItem, ymd: string, h: number): number {
  const ranges = (usableHours(it)?.[DAY_KEYS[dowOfYmd(ymd)]] ?? []).filter(([o, c]) => c - o >= MIN_VISIT_MS / 3600e3)
  if (!ranges.length || ranges.some(([o, c]) => o <= h && h + MIN_VISIT_MS / 3600e3 <= c)) return h
  const later = ranges.filter(([o]) => o > h).sort((a, b) => a[0] - b[0])[0]
  if (later) return later[0]
  const last = [...ranges].sort((a, b) => b[1] - a[1])[0]
  return Math.max(last[0], last[1] - 1)
}
export function daytimeStart(it: FitItem & DaypartItem, ymd: string): number | null {
  if (EVENING_CATS.has(it.category ?? '')) return null
  if (/\b(evening|night|nightly|after dark|late)\b/i.test(`${it.title ?? ''} ${it.one_line ?? ''}`)) return null
  // B-144: a dinner, a bar or anything else whose part of day is the evening is never given a 10 AM start; food goes at
  // lunch or dinner by the server's own hours (never the 10 AM default)
  const market = it.category === 'market' || /\b(?:farmers'?|flea)\s+markets?\b/i.test(it.title ?? '')
  if (eveningPart(it) || (it.category === 'food' && !market)) return null
  const today = usableHours(it)?.[DAY_KEYS[dowOfYmd(ymd)]]
  // markets go in the morning, at opening (journey J3: a 9-2 farmers market starts the day at 9)
  if (Array.isArray(today) && today.length) {
    const [open, close] = today[0]
    const start = market ? Math.max(open, 8) : Math.max(open, 10)
    return start + 1 <= close && start <= 16 ? start : null
  }
  // markets with no hours known go in the morning (blind test r3, Bree: a farmers market at 2 PM)
  if (market) return DAY_START_HOUR
  return 10
}


// ---------- R-98, R-106 (Round 3 lane 1): a listing's own time, and its next real date ----------
const CLOCK_RX = /\b(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?(?![a-z])/gi
/** Every clock time (am/pm) written in its own words: title, line, schedule. Never its venue's hours text. */
export function ownClocks(it: Pick<FitItem, 'title' | 'one_line' | 'schedule_text'>): number[] {
  const text = [it.title, it.one_line, it.schedule_text].filter(Boolean).join(' · ')
  const out: number[] = []
  for (const m of text.matchAll(CLOCK_RX)) {
    const h = Number(m[1]), mi = m[2] ? Number(m[2]) : 0
    if (h < 1 || h > 12 || mi > 59) continue
    out.push((h % 12) + (m[3].toLowerCase() === 'p' ? 12 : 0) + mi / 60)
  }
  return out
}
/** Evening kinds (a show, a set, a party): a start between 1 and 9 AM is almost surely a stored AM/PM slip (B-115: ENCORE!,
 *  a museum performance whose flyer says 7 PM, stored at 7:00 AM). */
const SHOW_CATS = new Set(['theater', 'comedy', 'nightlife', 'music', 'dance', 'film'])
const SHOW_WORDS = /\b(?:perform(?:ance|ances|s|ing)?|show|concert|gig|play|musical|theat(?:er|re)|comedy|stand-?up|improv|dj|dance party|screening|cabaret|burlesque|drag)\b/i
/**
 * The start a card may show for a dated event (R-98, B-115). Its stored start, unless:
 *  - its own words name the same clock hour on the other half of the day (stored 7 AM, its line says "7 PM"): that one;
 *  - it is an evening kind starting 1-9 AM and its words name no time that agrees: the day only, no time (never a wrong one).
 * `time` false = show the day without a time. Facts stay sourced: the corrected time is the one its own text states.
 */
export function ownStart(it: FitItem & { category?: string | null }, tz: string | null | undefined): { at: string; time: boolean } | null {
  if (it.kind !== 'event' || !it.start_at) return null
  const at = it.start_at
  const h = hourIn(at, tz)
  if (h === 0) return { at, time: false } // a date without a time
  const said = ownClocks(it)
  const same = (a: number, b: number) => Math.abs(a - b) < 1 / 120
  if (said.some((x) => same(x, h))) return { at, time: true }
  const flip = h < 12 ? h + 12 : h - 12
  if (said.some((x) => same(x, flip))) return { at: new Date(new Date(at).getTime() + (h < 12 ? 12 : -12) * 3600e3).toISOString(), time: true }
  const show = SHOW_CATS.has(String(it.category ?? '')) || SHOW_WORDS.test(`${it.title ?? ''} ${it.one_line ?? ''}`)
  if (show && h >= 1 && h < 9) return { at, time: false }
  return { at, time: true }
}

/**
 * The weekdays a place runs on by its OWN schedule when its hours are its venue's (R-98, B-137: "Wednesday CraftNight at
 * Akbar" carried the bar's "Daily 4pm-2am"), or a weekly thing with no hours at all. With its clock time when its own words
 * give exactly one. null = its hours (or Any day) describe it.
 */
export function ownWeekly(it: FitItem): { days: number[]; hour: number | null } | null {
  if (it.kind === 'event' && it.start_at) return null
  if (textDates(it)) return null
  const days = ownWeekdays(it)
  if (!days) return null
  const hours = usableHours(it)
  // hours open on other days too = the venue's hours, not the night's; hours only on its own days already say it
  if (hours && !DAY_KEYS.some((k, i) => !days.includes(i) && (hours[k] ?? []).length > 0)) return null
  const clocks = [...new Set(ownClocks(it))]
  return { days, hour: clocks.length === 1 ? clocks[0] : null }
}

const WEEKLY_ONE = new RegExp(WEEKLY_CADENCE.source, 'i')
/** The next weekday from `days` on or after a day. */
function nextWeekday(from: string, days: number[]): string {
  let d = from
  for (let i = 0; i < 7 && !days.includes(dowOfYmd(d)); i++) d = plusDays(d, 1)
  return d
}
/**
 * The next real day it happens on, on or after today (R-106, B-117), for Saved:
 *  { ymd, hour }  its next day (hour null = no time we know);
 *  { none: true, last }  nothing ahead (a one-off that has passed, dates in its text that have all passed);
 *  null  any day it is open (its hours say when).
 * A thing whose listed dates have all passed but whose words make it weekly ("Free Saturday class", "every Sunday") gets
 * its next such weekday: its schedule says it repeats.
 */
export type NextOn = { ymd: string; hour: number | null } | { none: true; last: string | null } | null
export function nextOn(it: FitItem & { category?: string | null }, tz: string | null | undefined, now = new Date()): NextOn {
  const today = ymdIn(now, tz)
  if (it.kind === 'event' && it.start_at) {
    const end = it.end_at ? new Date(it.end_at).getTime() : new Date(it.start_at).getTime() + 3 * 3600e3
    const startDay = ymdIn(it.start_at, tz)
    if (end < now.getTime()) return { none: true, last: startDay }
    if (fixedDay(it, tz)) {
      const own = ownStart(it, tz)
      return { ymd: startDay, hour: own && own.time ? hourIn(own.at, tz) : null }
    }
    return { ymd: startDay > today ? startDay : today, hour: null } // a run: on now or from its first day
  }
  const t = textDates(it, now, tz)
  if (t) {
    const ahead = t.days.filter((d) => d >= today)
    if (ahead.length) return { ymd: ahead[0], hour: ahead.length === 1 || t.days.length === 1 ? t.hour : null }
    const wd = textWeekdays(it)
    if (wd && (WEEKLY_ONE.test(words(it)) || weekdaysIn([it.title]))) return { ymd: nextWeekday(today, wd), hour: null }
    return { none: true, last: t.days[t.days.length - 1] }
  }
  const weekly = ownWeekly(it)
  if (weekly) return { ymd: nextWeekday(today, weekly.days), hour: weekly.hour }
  return null
}

/**
 * A title without the source's relative-time aside (R-98, B-135: "System of a Down: 25 Years of Toxicity (next week)" sat in
 * This weekend). "Next week" was true on the day the source wrote it; the date we show is the fact. Only a bracketed or
 * trailing aside goes; a title that IS such words is kept.
 */
const RELATIVE_ASIDE = /\s*(?:\(\s*|\[\s*|[-–—:|]\s+)(?:next|this|coming)\s+(?:week(?:end)?|month|sat(?:urday)?|sun(?:day)?|mon(?:day)?|tue(?:s(?:day)?)?|wed(?:nesday)?|thu(?:rs(?:day)?)?|fri(?:day)?)\s*[)\]]?\s*$|\s*(?:\(\s*|\[\s*|[-–—:|]\s+)(?:tonight|tomorrow|today)\s*[!)\]]?\s*$/i
export function cleanTitle(title: string): string {
  const t = title.replace(RELATIVE_ASIDE, '').trim()
  return t.length >= 3 ? t : title
}
